import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// src/lib/exporters.js
const PDF_PAGE_FORMAT = 'a4';
const PDF_MARGIN_PT = 40;
const PT_TO_PX = 96 / 72;

function buildDiaryExportHtml({ title, bodyHtml, bodyText, dateString, author = '' }) {
  const safeHtml = sanitizeRichHtml(bodyHtml) || escapeHtml(bodyText || '');
  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title || 'Diary')}</title>
        <style>
          @page { margin: 20mm; }
          body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #111111; }
          h1 { font-size: 22px; margin: 0 0 8px; }
          .meta { color: #555; font-size: 12px; margin-bottom: 16px; }
          .content { line-height: 1.6; font-size: 14px; white-space: pre-wrap; color: #111111; }
          .content * { font-family: inherit; }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(title || '')}</h1>
        <div class="meta">${escapeHtml(dateString || '')}${author ? ' • ' + escapeHtml(author) : ''}</div>
        <div class="content">${safeHtml}</div>
        <script>
          window.onload = () => {
            try {
              window.focus();
              window.print();
              setTimeout(() => window.close && window.close(), 300);
            } catch (e) {}
          };
        </script>
      </body>
    </html>
  `;
}

function parseResolvedCssColor(value) {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized || normalized === 'transparent') {
    return { r: 0, g: 0, b: 0, a: 0 };
  }

  const rgb = normalized.match(/^rgb\(\s*(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})\s*\/\s*([0-9.]+)\s*\)$/i)
    || normalized.match(/^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})(?:[\s,/]+([0-9.]+))?\s*\)$/i);
  if (!rgb) return null;

  const clamp = (n) => Math.max(0, Math.min(255, Number.parseInt(n, 10) || 0));
  const alpha = rgb[4] == null ? 1 : Math.max(0, Math.min(1, Number.parseFloat(rgb[4]) || 0));
  return {
    r: clamp(rgb[1]),
    g: clamp(rgb[2]),
    b: clamp(rgb[3]),
    a: alpha,
  };
}

function relativeLuminance({ r, g, b }) {
  const channel = (value) => {
    const srgb = value / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return (0.2126 * channel(r)) + (0.7152 * channel(g)) + (0.0722 * channel(b));
}

function shouldForceReadableText(color) {
  if (!color) return false;
  if (color.a < 0.95) return true;
  return relativeLuminance(color) > 0.72;
}

function normalizeExportTextStyles(host) {
  const contentRoot = host.querySelector('.content');
  if (!contentRoot) return;

  const elements = [contentRoot, ...contentRoot.querySelectorAll('*')];
  elements.forEach((element) => {
    element.style.opacity = '1';
    element.style.filter = 'none';
    element.style.mixBlendMode = 'normal';

    if (element.style.backgroundColor) {
      const background = parseResolvedCssColor(element.style.backgroundColor);
      if (!background || background.a < 0.01) {
        element.style.backgroundColor = '';
      }
    }

    const computed = window.getComputedStyle(element);
    const resolvedColor = parseResolvedCssColor(computed.color);
    if (shouldForceReadableText(resolvedColor)) {
      element.style.color = '#111111';
    }
  });
}

export async function makeDiaryPdfBlob({ title, bodyHtml, bodyText, dateString, author = '' }) {
  const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: PDF_PAGE_FORMAT });
  const pageWidthPt = pdf.internal.pageSize.getWidth();
  const pageHeightPt = pdf.internal.pageSize.getHeight();
  const contentWidthPt = pageWidthPt - (PDF_MARGIN_PT * 2);
  const contentHeightPt = pageHeightPt - (PDF_MARGIN_PT * 2);
  const contentWidthPx = Math.round(contentWidthPt * PT_TO_PX);

  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.style.width = `${contentWidthPx}px`;
  host.style.background = '#ffffff';
  host.style.zIndex = '-1';
  host.style.padding = '0';
  host.innerHTML = buildDiaryExportHtml({ title, bodyHtml, bodyText, dateString, author });

  document.body.appendChild(host);

  try {
    normalizeExportTextStyles(host);
    const body = host.querySelector('body');
    const target = body || host;
    const canvas = await html2canvas(target, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
    });
    const scale = contentWidthPt / canvas.width;
    const pageContentHeightPx = Math.max(1, Math.floor(contentHeightPt / scale));
    let offsetY = 0;
    let pageIndex = 0;

    while (offsetY < canvas.height) {
      const sliceHeightPx = Math.min(pageContentHeightPx, canvas.height - offsetY);
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeightPx;

      const pageContext = pageCanvas.getContext('2d');
      if (!pageContext) {
        throw new Error('Unable to prepare PDF page canvas.');
      }

      pageContext.fillStyle = '#ffffff';
      pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      pageContext.drawImage(
        canvas,
        0,
        offsetY,
        canvas.width,
        sliceHeightPx,
        0,
        0,
        pageCanvas.width,
        sliceHeightPx
      );

      const imageHeightPt = sliceHeightPx * scale;
      const imgData = pageCanvas.toDataURL('image/jpeg', 0.95);
      if (pageIndex > 0) {
        pdf.addPage(PDF_PAGE_FORMAT, 'p');
      }
      pdf.addImage(imgData, 'JPEG', PDF_MARGIN_PT, PDF_MARGIN_PT, contentWidthPt, imageHeightPt);

      offsetY += sliceHeightPx;
      pageIndex += 1;
    }

    return pdf.output('blob');
  } finally {
    host.remove();
  }
}

// ---------- PDF (robust: Blob URL + iframe fallback) ----------
export function printDiaryAsPdf({ title, bodyHtml, bodyText, dateString, author = '' }) {
  const html = buildDiaryExportHtml({ title, bodyHtml, bodyText, dateString, author });

  // Approach A: open a Blob URL in a new tab (no noreferrer/noopener)
  try {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank'); // do NOT pass 'noreferrer'
    if (!w) throw new Error('popup-blocked');
    setTimeout(() => URL.revokeObjectURL(url), 15000);
    return;
  } catch (e) {
    // fall through to iframe
  }

  // Approach B: hidden iframe fallback
  try {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    if ('srcdoc' in iframe) {
      iframe.srcdoc = html;
    } else {
      const blob = new Blob([html], { type: 'text/html' });
      iframe.src = URL.createObjectURL(blob);
      setTimeout(() => URL.revokeObjectURL(iframe.src), 15000);
    }

    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(() => iframe.remove(), 1000);
      }
    };
  } catch {
    alert('Unable to open the PDF preview. Please allow pop-ups for this site.');
  }
}

// ---------- DOCX (via docx + file-saver; lazy loaded) ----------
export async function exportDiaryToDocx({ title, bodyHtml, bodyText, dateString, author = '' }) {
  const [{ Document, Paragraph, Packer, HeadingLevel, TextRun }, { saveAs }] = await Promise.all([
    import('docx'),
    import('file-saver'),
  ]);

  const paragraphs = [];

  if (title) {
    paragraphs.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun({ text: title, bold: true })],
        spacing: { after: 200 },
      })
    );
  }

  const metaLine = [dateString, author].filter(Boolean).join(' • ');
  if (metaLine) {
    paragraphs.push(
      new Paragraph({
        children: [new TextRun({ text: metaLine, italics: true, color: '666666' })],
        spacing: { after: 200 },
      })
    );
  }

  (String(bodyText || '').split(/\r?\n/)).forEach((line) => {
    paragraphs.push(new Paragraph({ children: [new TextRun({ text: line })] }));
  });

  const doc = new Document({
    sections: [{ properties: {}, children: paragraphs }],
  });

  const blob = await Packer.toBlob(doc);
  const safeTitle = (title || 'Diary').replace(/[\\/:*?"<>|]+/g, '_').slice(0, 60);
  saveAs(blob, `${safeTitle}.docx`);
}

// ---------- util ----------
function escapeHtml(s) {
  return String(s || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

// minimal allowlist sanitizer to keep basic inline formatting for exports
function sanitizeRichHtml(html = '') {
  if (!html) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const allowedTags = new Set(['B', 'I', 'U', 'STRONG', 'EM', 'SPAN', 'DIV', 'P', 'BR']);
    const allowedStyles = ['color', 'background-color', 'font-weight', 'font-style', 'text-decoration'];

    const serializeNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.textContent || '');
      if (node.nodeType !== Node.ELEMENT_NODE) return '';

      const tag = node.tagName.toUpperCase();
      if (!allowedTags.has(tag)) {
        return Array.from(node.childNodes).map(serializeNode).join('');
      }

      if (tag === 'BR') return '<br />';

      const style = node.getAttribute('style') || '';
      const tmp = document.createElement('div');
      tmp.setAttribute('style', style);
      const kept = [];
      allowedStyles.forEach((prop) => {
        const v = tmp.style.getPropertyValue(prop);
        if (v) kept.push(`${prop}: ${v}`);
      });
      const styleAttr = kept.length ? ` style="${kept.join('; ')}"` : '';
      const children = Array.from(node.childNodes).map(serializeNode).join('');
      return `<${tag.toLowerCase()}${styleAttr}>${children}</${tag.toLowerCase()}>`;
    };

    return Array.from(doc.body.firstChild?.childNodes || []).map(serializeNode).join('');
  } catch {
    return '';
  }
}
